import { getStage } from '../../lib/runHelpers';
import React from 'react';
import {
  Upload,
  Link as LinkIcon,
  FileText,
  ExternalLink,
  AlertCircle,
} from 'lucide-react';
import { getRunTitle, formatDate } from '../../lib/runHelpers';

export const InfoTab = ({ run, status, StatusIcon, tone, showResultLinks = true }) => {
  const SourceIcon = run.sourceFileType === 'upload' ? Upload : LinkIcon;
  const title = getRunTitle(run);

  const shortsCount = getStage(run, 'shorts').shortsCount;
  const shortsSucceeded = getStage(run, 'shorts').shortsSucceeded;

  return (
    <div className="p-4 sm:p-8">
      {/* Compact status indicator */}
      <div className={`inline-flex items-center gap-3 px-4 py-2.5 ${tone.bg} ${tone.border} border rounded-2xl mb-6`}>
        <StatusIcon
          className={`w-5 h-5 ${tone.text} ${status.spinning ? 'animate-spin' : ''}`}
          strokeWidth={2}
        />
        <span className={`text-sm font-medium ${tone.text}`}>{status.label}</span>
        <span className={`w-1.5 h-1.5 rounded-full ${tone.dot} ${status.spinning ? 'animate-pulse' : ''}`} />
      </div>

      <div className="space-y-4 sm:space-y-6">
        <div>
          <h1
            dir="auto"
            className="text-2xl sm:text-3xl font-bold text-white mb-2 sm:mb-3 pe-12 break-words text-start"
          >
            {title}
          </h1>
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-slate-400 text-sm sm:text-base">
            <span className="flex items-center gap-2">
              <SourceIcon className="w-4 h-4 text-orange-400" />
              {run.sourceFileType === 'upload' ? 'Direct upload' : 'Drive link'}
            </span>
            <span>•</span>
            <span>{formatDate(run.createdAt)}</span>
          </div>
        </div>

        {run.errorMessage && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 sm:p-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold text-red-300 mb-1">Error</h3>
              <p dir="auto" className="text-sm text-red-200/90 break-words">
                {run.errorMessage}
              </p>
            </div>
          </div>
        )}

        <div className="bg-brand-surface rounded-xl p-4 sm:p-6 border border-brand-surface-3">
          <h3 className="text-base sm:text-lg font-semibold text-white mb-3">Source</h3>
          <dl className="space-y-2 text-sm sm:text-base">
            {run.originalFileName && (
              <div className="flex justify-between gap-3">
                <dt className="text-slate-400 flex-shrink-0">File name</dt>
                <dd dir="auto" className="text-white text-right break-all">
                  {run.originalFileName}
                </dd>
              </div>
            )}
            <div className="flex justify-between gap-3">
              <dt className="text-slate-400">Source type</dt>
              <dd className="text-white capitalize">{run.sourceFileType || 'drive'}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-slate-400">Created</dt>
              <dd className="text-white">{formatDate(run.createdAt)}</dd>
            </div>
            {typeof run.n8nExecutionDurationMinutes === 'number' && (
              <div className="flex justify-between gap-3">
                <dt className="text-slate-400">Run time</dt>
                <dd className="text-white">
                  {run.n8nExecutionDurationMinutes.toFixed(1)} min
                </dd>
              </div>
            )}
            {typeof shortsCount === 'number' && (
              <div className="flex justify-between gap-3">
                <dt className="text-slate-400">Shorts</dt>
                <dd className="text-white">
                  {shortsSucceeded ?? 0} / {shortsCount}
                </dd>
              </div>
            )}
          </dl>
        </div>

        {showResultLinks && (run.resultTranscriptUrl || run.resultMappingUrl) && (
          <div className="bg-brand-surface rounded-xl p-4 sm:p-6 border border-brand-surface-3">
            <h3 className="text-base sm:text-lg font-semibold text-white mb-3">Results</h3>
            <div className="flex flex-col sm:flex-row gap-3">
              {run.resultTranscriptUrl && (
                <a
                  href={run.resultTranscriptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-brand-surface-2 hover:bg-brand-surface-3 text-white rounded-xl transition-colors duration-200 text-sm font-medium"
                >
                  <FileText className="w-4 h-4 text-orange-400" />
                  Open transcript
                  <ExternalLink className="w-3.5 h-3.5 opacity-60" />
                </a>
              )}
              {run.resultMappingUrl && (
                <a
                  href={run.resultMappingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-xl transition-all duration-200 text-sm font-medium"
                >
                  <FileText className="w-4 h-4" />
                  Open mapping
                  <ExternalLink className="w-3.5 h-3.5 opacity-80" />
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
